import Typography from "@mui/material/Typography";
import { useTiers } from "api/tiers";
import { useAppSelector } from "app/hooks";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { useMessage } from "hooks/intl";
import { useCallback } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  DateFilterValue,
  DateOperators,
  ExportListItem,
  ExportType,
} from "types/exports";
import { useStyles } from "./ExportList.styles";
import { filterIsShown, getFilterLabel } from "./helpers";

const typesExportedWithFilters = [
  ExportType.MEMBER,
  ExportType.CAMPAIGN_CODE,
  ExportType.REWARD_FULFILLMENT,
];

type ExportTypeTooltipProps = {
  item: ExportListItem;
  typeLabel: string;
};

export const ExportTypeTooltip = ({
  item,
  typeLabel,
}: ExportTypeTooltipProps) => {
  const { message } = useMessage();
  const classes = useStyles();
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { useListOfTiers } = useTiers();
  const { data: tiersList } = useListOfTiers();

  const areFiltersVisible =
    item.criteria && typesExportedWithFilters.includes(item.type);

  const parsedFiltersCriteria = JSON.parse(item.criteria);

  const showElementName = () => {
    switch (item.type) {
      case ExportType.MEMBER_SEGMENT:
        return parsedFiltersCriteria.segmentName;
      case ExportType.MEMBER_TIER:
        return parsedFiltersCriteria.tierName;
      default:
        return "";
    }
  };

  const getDateValue = useCallback(
    (value: DateFilterValue) => {
      const getDateLabel = (key: string, value: string) => {
        switch (key) {
          case DateOperators.LESS:
            return `${message({ id: "before" })} ${value}`;
          case DateOperators.LESS_OR_EQUAL:
            return `${message({ id: "beforeOrEqual" })} ${value}`;
          case DateOperators.GREATER:
            return `${message({ id: "after" })} ${value}`;
          case DateOperators.GREATER_OR_EQUAL:
            return `${message({ id: "afterOrEqual" })} ${value}`;
          default:
            return String(value);
        }
      };

      const filters = Object.entries(value)
        .map(([key, value]) => getDateLabel(key, value))
        .join(", ");
      return filters;
    },
    [message],
  );

  const getNumberValue = useCallback(
    (value: any) => {
      const filters = Object.entries(value)
        .map(([key, value]) => `${message({ id: key })}: ${value}`)
        .join(", ");

      return filters;
    },
    [message],
  );

  const getFilterValue = useCallback(
    ({ key, value }: { key: string; value: any }) => {
      switch (key) {
        case "amountExcludedForTier":
        case "transactionsCount":
        case "activeUnitSingularOrPlural":
        case "avgTransactionValueWithCurrency":
        case "transactionsAmount":
        case "totalEarnedUnits":
        case "transactionsAmountWithoutDeliveryCosts":
        case "priceInUnits":
          return getNumberValue(value);
        case "usedOn":
        case "createdOn":
        case "updatedAt":
        case "generatedOn":
        case "firstTransactionDate":
        case "lastTransactionDate":
        case "tierFromDefaultTierSetReachedDate":
        case "dateOfBirth":
        case "redemptionDate":
        case "registrationDate":
          return getDateValue(value);
        case "agreement1":
        case "agreement2":
        case "agreement3":
          return message({ id: value === "true" ? "active" : "inactive" });
        case "country":
          return value;
        case "tierName":
          return tiersList?.items.find((tier) => tier.levelId === value)?.name;
        case "rewardType":
          return message({ id: value });
        default:
          return String(value);
      }
    },
    [getDateValue, getNumberValue, message, tiersList?.items],
  );

  const getFilterKey = useCallback(
    (key: string) => {
      switch (key) {
        case "activeUnitSingularOrPlural":
          return message(
            { id: "activeUnitSingularOrPlural" },
            { units: defaultWallet?.unitPluralName },
          );
        case "avgTransactionValueWithCurrency":
          return message(
            { id: "avgTransactionValueWithCurrency" },
            { currency },
          );
        case "totalEarnedUnits":
          return message(
            { id: "totalEarnedUnits" },
            { units: defaultWallet?.unitPluralName },
          );
        case "priceInUnits":
          return message(
            { id: "priceInUnits" },
            { units: defaultWallet?.unitPluralName },
          );
        default:
          return message({ id: key });
      }
    },
    [message, defaultWallet?.unitPluralName, currency],
  );

  const getFilters = useCallback(() => {
    const filters = Object.keys(parsedFiltersCriteria)
      .map(
        (key) =>
          filterIsShown(key, item.type) && {
            key: getFilterLabel(item, key),
            value: parsedFiltersCriteria[key],
          },
      )
      .filter((filter) => filter);
    return filters.map(
      (filter) =>
        filter && `${getFilterKey(filter.key)}: ${getFilterValue(filter)}`,
    );
  }, [item, parsedFiltersCriteria, getFilterValue, getFilterKey]);

  const filterList = getFilters();

  return (
    <TooltipWithIcon
      isWhite
      icon={<InfoCircleIcon />}
      text={
        <>
          <Typography className={classes.tooltipTitle}>
            {message({ id: typeLabel })}
          </Typography>

          <Typography className={classes.tooltipContent}>
            <>
              {areFiltersVisible &&
                filterList.map((filter, index) => (
                  <span key={index} className={classes.tooltipFilter}>
                    {filter}
                  </span>
                ))}
              {showElementName()}
            </>
          </Typography>
        </>
      }
      placement="bottom-start"
      className={classes.tooltipIcon}
      data-test-id={`export-list-${item.type}-tooltip-${item.exportId}`}
    />
  );
};
